import {
  ReviewHeartRhythmMeasurementProps,
  HeartRhythmMeasurement,
  HeartRhythmMeasurementStatus,
} from 'models/heartRhythm/measurement';
import { rqlBuilder } from '@extrahorizon/javascript-sdk';
import { MeasurementData } from 'models/measurement';
import { assertAffectedRecord, SCHEMA_NAMES, sdk } from 'utils/query';
import dayjs from 'dayjs';
import retry from 'async-retry';
import { TransitionError } from 'services/errors/TransitionError';
import { distributionMeasurementIndicators } from 'utils/measurement';
import { PatientMeasurement } from 'utils/models';
import { chunk } from 'lodash';
import { ConcurrentQueue, DEFAULT_CONCURRENCY_LIMIT } from 'utils/concurrentQueue';
import { HeartRhythmMeasurementDiagnosisMap } from 'models/heartRhythm/measurementDiagnosis';
import { EmptyParametersError } from '../errors/EmptyParametersError';
import { NoMeasurementsError } from '../errors/NoMeasurementsError';

const MAX_GROUP_BATCH_SIZE = 5;

export interface GetHeartRhythmMeasurementDetailsParams {
  measurementId?: string;
  patientId?: string;
}

export const getHeartRhythmMeasurementDetails = async (
  params: GetHeartRhythmMeasurementDetailsParams,
): Promise<HeartRhythmMeasurement> => {
  const { measurementId, patientId } = params;
  if (!measurementId) throw new EmptyParametersError();

  const patientIdQuery = patientId
    ? rqlBuilder().eq('creatorId', patientId).intermediate()
    : rqlBuilder().intermediate();

  const rql = rqlBuilder()
    .select([
      'id',
      'creatorId',
      'groupIds',
      'updateTimestamp',
      'data.heartrate',
      'data.app',
      'data.indicator',
      'data.measurement_timestamp',
      'data.diagnosis',
      'status',
      'creationTimestamp',
      'data.reviewRequestedTime',
      'data.context',
      'data.device',
      'data.ppg',
      'data.time',
      'data.peaks',
      'data.indicator_algorithm',
      'data.algorithm',
      'data.indicator_probability',
      'data.flags',
      'data.claim',
      'data.tags',
      'data.reviewDuration',
      'data.viewResult',
      'data.reviewer_user_id',
      'data.user',
      'data.mostSevereLabel',
    ])
    .eq('id', measurementId)
    .and(patientIdQuery)
    .build();

  const result = await sdk.data.documents.findFirst<MeasurementData, HeartRhythmMeasurementStatus>(
    SCHEMA_NAMES.heartRhythm,
    {
      rql,
    },
  );

  if (!result) throw new NoMeasurementsError();
  return result;
};

export interface GetHeartRhythmMeasurementsParams {
  patientId?: string;
  groupIds?: string[];
  startDate: Date;
  endDate: Date;
}

export const getHeartRhythmMeasurements = async (
  params: GetHeartRhythmMeasurementsParams,
): Promise<HeartRhythmMeasurement[]> => {
  const { patientId, groupIds, startDate, endDate } = params;
  if (!patientId || !groupIds || !startDate || !endDate) throw new EmptyParametersError();

  const allMeasurements: HeartRhythmMeasurement[] = [];

  const fieldsToSelect = [
    'id',
    'status',
    'groupIds',
    'updateTimestamp',
    'data.measurement_timestamp',
    'data.indicator',
    'data.heartrate',
    'data.diagnosis',
    'data.context',
    'data.mostSevereLabel',
  ];

  const queue = new ConcurrentQueue<HeartRhythmMeasurement[]>({
    concurrencyLimit: DEFAULT_CONCURRENCY_LIMIT,
    onComplete: async (measurements) => {
      allMeasurements.push(...measurements);
    },
  });

  const indicators = distributionMeasurementIndicators();
  const groupIdBatches = chunk(groupIds, MAX_GROUP_BATCH_SIZE);

  const tasks = indicators.flatMap((indicator) =>
    groupIdBatches.map((batchedGroupIds) =>
      queue.push(async () => {
        const rql = rqlBuilder()
          .eq('creatorId', patientId)
          .eq('data.indicator', indicator)
          .in('groupIds', batchedGroupIds)
          .ge('data.measurement_timestamp', `${dayjs(startDate).unix() * 1000}`)
          .le('data.measurement_timestamp', `${dayjs(endDate).unix() * 1000}`)
          .sort('data.measurement_timestamp')
          .select(fieldsToSelect)
          .build();

        return sdk.data.documents.findAll<MeasurementData, HeartRhythmMeasurementStatus>(SCHEMA_NAMES.heartRhythm, {
          rql,
        });
      }),
    ),
  );

  await Promise.all(tasks);

  return allMeasurements.sort((a, b) => (a.data?.measurement_timestamp ?? 0) - (b.data?.measurement_timestamp ?? 0));
};

export interface GetHeartRhythmMeasurementIdByTimestampParams {
  patientId?: string;
  measurement?: PatientMeasurement;
}

export const getHeartRhythmMeasurementIdByTimestamp = async (params: GetHeartRhythmMeasurementIdByTimestampParams) => {
  const { patientId, measurement } = params;
  if (!patientId || !measurement) throw new EmptyParametersError();

  const rql = rqlBuilder()
    .select(['id'])
    .eq('creatorId', patientId)
    .ge('data.measurement_timestamp', measurement.timestamp.toString())
    .lt('data.measurement_timestamp', (measurement.timestamp + 1).toString())
    .build();

  const result = await sdk.data.documents.findFirst(SCHEMA_NAMES.heartRhythm, { rql });

  if (!result) throw new NoMeasurementsError();
  return result.id;
};

export const reviewHeartRhythmMeasurement = async (params: ReviewHeartRhythmMeasurementProps) => {
  const { label, measurement, text } = params;
  if (!label || !measurement) throw new EmptyParametersError();

  let duration = measurement.data?.reviewDuration;

  if (!duration) {
    if (measurement?.data?.reviewRequestedTime) {
      duration = Date.now() - measurement.data.reviewRequestedTime;
    } else if (measurement?.creationTimestamp) {
      duration = Date.now() - new Date(measurement.creationTimestamp).getTime();
    }
  }

  const { indicator } = HeartRhythmMeasurementDiagnosisMap[label];

  const result = await sdk.data.documents.transition(SCHEMA_NAMES.heartRhythm, measurement.id, {
    name: 'review',
    data: { indicator, diagnosis: { text, label: [label] }, reviewer_user_id: '', reviewDuration: duration },
  });

  await retry(
    async () => {
      const rql = rqlBuilder().eq('id', measurement.id).build();
      const updatedMeasurement = await sdk.data.documents.findFirst<MeasurementData>(SCHEMA_NAMES.heartRhythm, { rql });

      if (updatedMeasurement?.data?.diagnosis?.label[0] === label) return;
      throw new TransitionError();
    },
    {
      retries: 15,
      minTimeout: 100,
      maxTimeout: 500,
    },
  );

  return assertAffectedRecord({ result });
};
